import React, { useState, useEffect } from 'react';
import {
  Card,
  CardBody,
  CardTitle,
  Table,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Alert,
  Spinner,
  Badge
} from 'reactstrap';
import axios from '../../api/Axios';
const ManageBMOOfficers = () => {
  const [officers, setOfficers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [bmoDomain, setBmoDomain] = useState('');
  
  // Modal state
  const [modal, setModal] = useState(false);
  const [creating, setCreating] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    title: '',
    phone_number: '',
    email_address: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [emailChecking, setEmailChecking] = useState(false);

  useEffect(() => {
    fetchOfficers();
    fetchBMOInfo();
  }, []);

  const fetchBMOInfo = async () => {
    try {
      // Get BMO information from the dashboard endpoint
      const response = await axios.get('/api/accounts/bmo-user-admin/dashboard/');
      if (response.data.bmoInfo && response.data.bmoInfo.domain) {
        setBmoDomain(response.data.bmoInfo.domain);
      }
    } catch (error) {
      console.error('Error fetching BMO info:', error);
      // Don't show error for this as it's not critical
    }
  };

  const fetchOfficers = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/accounts/bmo-officers/');
      console.log('BMO Officers response:', response.data);
      setOfficers(response.data);
      setError('');
    } catch (error) {
      console.error('Error fetching BMO officers:', error);
      setError('Failed to load BMO officers. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const toggleModal = () => {
    setModal(!modal);
    if (!modal) {
      // Reset form when opening modal
      setFormData({
        first_name: '',
        last_name: '',
        title: '',
        phone_number: '',
        email_address: ''
      });
      setFormErrors({});
      setSuccess('');
      setError('');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear specific field error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Check email availability when email changes
    if (name === 'email_address' && value) {
      checkEmailAvailability(value);
    }
  };

  const checkEmailAvailability = async (email) => {
    if (!email || !email.includes('@')) return;
    
    setEmailChecking(true);
    try {
      // First check domain validation
      if (bmoDomain && email.includes('@')) {
        const emailDomain = email.split('@')[1];
        if (emailDomain.toLowerCase() !== bmoDomain.toLowerCase()) {
          setFormErrors(prev => ({
            ...prev,
            email_address: `Email must use the BMO domain: @${bmoDomain}`
          }));
          setEmailChecking(false);
          return;
        }
      }
      
      // Then check email availability
      const response = await axios.post('/api/accounts/check-email/', {
        email_address: email
      });
      
      if (response.data.exists) {
        setFormErrors(prev => ({
          ...prev,
          email_address: 'This email address is already registered'
        }));
      } else {
        setFormErrors(prev => ({
          ...prev,
          email_address: ''
        }));
      }
    } catch (error) {
      console.error('Error checking email:', error);
    } finally {
      setEmailChecking(false);
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.first_name.trim()) {
      errors.first_name = 'First name is required';
    }
    
    if (!formData.last_name.trim()) {
      errors.last_name = 'Last name is required';
    }
    
    if (!formData.title.trim()) {
      errors.title = 'Title is required';
    }
    
    if (!formData.phone_number.trim()) {
      errors.phone_number = 'Phone number is required';
    }
    
    if (!formData.email_address.trim()) {
      errors.email_address = 'Email address is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email_address)) {
      errors.email_address = 'Please enter a valid email address';
    } else if (bmoDomain && formData.email_address.includes('@')) {
      const emailDomain = formData.email_address.split('@')[1];
      if (emailDomain.toLowerCase() !== bmoDomain.toLowerCase()) {
        errors.email_address = `Email must use the BMO domain: @${bmoDomain}`;
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (formErrors.email_address) {
      setError('Please fix the email error before submitting');
      return;
    }

    setCreating(true);
    setError('');
    
    try {
      console.log('Creating BMO Officer:', formData);
      const response = await axios.post('/api/accounts/create-bmo-officer/', formData);
      console.log('BMO Officer created:', response.data);
      
      setSuccess(`BMO Officer "${formData.first_name} ${formData.last_name}" has been created successfully! An activation email has been sent to ${formData.email_address}.`);
      
      // Refresh the officers list
      await fetchOfficers();
      
      // Close modal after a short delay to let user see success message
      setTimeout(() => {
        toggleModal();
      }, 2000);
      
    } catch (error) {
      console.error('Error creating BMO Officer:', error);
      
      if (error.response?.data) {
        // Handle field-specific errors
        if (typeof error.response.data === 'object' && !error.response.data.error) {
          setFormErrors(error.response.data);
        } else {
          setError(error.response.data.error || 'Failed to create BMO Officer. Please try again.');
        }
      } else {
        setError('Failed to create BMO Officer. Please check your connection and try again.');
      }
    } finally {
      setCreating(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner color="primary" />
        <span className="ms-2">Loading BMO Officers...</span>
      </div>
    );
  }

  return (
    <div className="container-fluid px-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>BMO Officers Management</h2>
        <button type="button" className="d-flex align-items-center btn btn-primary" onClick={toggleModal}>
          <i className="fas fa-plus me-2"></i>ADD +
        </button>
      </div>

      {error && (
        <Alert color="danger" className="mb-4">
          <strong>Error:</strong> {error}
        </Alert>
      )}

      <Card>
        <CardBody>
          <CardTitle tag="h5">
            BMO Officers ({officers.length})
          </CardTitle>
          
          {officers.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-muted">No BMO Officers found.</p>
              <Button color="primary" onClick={toggleModal}>
                Create First BMO Officer
              </Button>
            </div>
          ) : (
            <div className="table-responsive">
              <Table striped hover>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Title</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Status</th>
                    <th>Created By</th>
                    <th>Created Date</th>
                  </tr>
                </thead>
                <tbody>
                  {officers.map((officer) => (
                    <tr key={officer.id}>
                      <td>
                        <strong>{officer.first_name} {officer.last_name}</strong>
                      </td>
                      <td>{officer.title || 'N/A'}</td>
                      <td>
                        <a href={`mailto:${officer.email_address}`}>
                          {officer.email_address}
                        </a>
                      </td>
                      <td>{officer.phone_number || 'N/A'}</td>
                      <td>
                        {officer.is_activated ? (
                          <Badge color="success">Activated</Badge>
                        ) : (
                          <Badge color="warning">Pending Activation</Badge>
                        )}
                      </td>
                      <td>{officer.created_by_email || 'N/A'}</td>
                      <td>{formatDate(officer.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Create BMO Officer Modal */}
      <Modal isOpen={modal} toggle={toggleModal} size="lg">
        <ModalHeader toggle={toggleModal}>
          Create New BMO Officer
        </ModalHeader>
        <Form onSubmit={handleSubmit}>
          <ModalBody>
            {success && (
              <Alert color="success">
                <strong>Success!</strong> {success}
              </Alert>
            )}
            
            {error && (
              <Alert color="danger">
                <strong>Error:</strong> {error}
              </Alert>
            )}

            <div className="row">
              <div className="col-md-6">
                <FormGroup>
                  <Label for="first_name">First Name *</Label>
                  <Input
                    type="text"
                    name="first_name"
                    id="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    invalid={!!formErrors.first_name}
                    placeholder="Enter first name"
                  />
                  {formErrors.first_name && (
                    <div className="invalid-feedback">{formErrors.first_name}</div>
                  )}
                </FormGroup>
              </div>
              
              <div className="col-md-6">
                <FormGroup>
                  <Label for="last_name">Last Name *</Label>
                  <Input
                    type="text"
                    name="last_name"
                    id="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    invalid={!!formErrors.last_name}
                    placeholder="Enter last name"
                  />
                  {formErrors.last_name && (
                    <div className="invalid-feedback">{formErrors.last_name}</div>
                  )}
                </FormGroup>
              </div>
            </div>

            <FormGroup>
              <Label for="title">Title *</Label>
              <Input
                type="text"
                name="title"
                id="title"
                value={formData.title}
                onChange={handleInputChange}
                invalid={!!formErrors.title}
                placeholder="Enter job title"
              />
              {formErrors.title && (
                <div className="invalid-feedback">{formErrors.title}</div>
              )}
            </FormGroup>

            <FormGroup>
              <Label for="phone_number">Phone Number *</Label>
              <Input
                type="tel"
                name="phone_number"
                id="phone_number"
                value={formData.phone_number}
                onChange={handleInputChange}
                invalid={!!formErrors.phone_number}
                placeholder="Enter phone number"
              />
              {formErrors.phone_number && (
                <div className="invalid-feedback">{formErrors.phone_number}</div>
              )}
            </FormGroup>

            <FormGroup>
              <Label for="email_address">Email Address *</Label>
              <div className="input-group">
                <Input
                  type="email"
                  name="email_address"
                  id="email_address"
                  value={formData.email_address}
                  onChange={handleInputChange}
                  invalid={!!formErrors.email_address}
                  placeholder="Enter email address"
                />
                {emailChecking && (
                  <div className="input-group-text">
                    <Spinner size="sm" />
                  </div>
                )}
              </div>
              {formErrors.email_address && (
                <div className="invalid-feedback d-block">{formErrors.email_address}</div>
              )}
              <small className="form-text text-muted">
                {bmoDomain ? (
                  <>Must use BMO domain: @{bmoDomain}. An activation email will be sent to this address.</>
                ) : (
                  'An activation email will be sent to this address'
                )}
              </small>
            </FormGroup>

            <Alert color="info" className="mt-3">
              <strong>Note:</strong> After creating the BMO Officer account, an activation email will be sent to the provided email address. The user will need to click the activation link and set their password before they can access the system.
            </Alert>
          </ModalBody>
          
          <ModalFooter>
            <Button color="secondary" onClick={toggleModal} disabled={creating}>
              Cancel
            </Button>
            <Button 
              color="primary" 
              type="submit" 
              disabled={creating || emailChecking || !!formErrors.email_address}
            >
              {creating ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Creating...
                </>
              ) : (
                <>
                  <i className="fas fa-user-plus me-2"></i>
                  Create BMO Officer
                </>
              )}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </div>
  );
};

export default ManageBMOOfficers;
